/**
 * Security helpers for Service Worker and script URLs.
 * Mitigates server-side reflected XSS where a URL parameter could cause
 * the worker to load scripts from an attacker-controlled origin.
 *
 * @see https://owasp.org/www-community/attacks/xss/
 */

/** Allowed hostname suffixes for worker script and API origins (e.g. *.cleverpush.com) */
export const ALLOWED_SCRIPT_ORIGIN_SUFFIXES = Object.freeze([
  '.cleverpush.com',
  '.mycleverpush.com'
]);

/**
 * Returns the hostname from a URL string, or null if invalid.
 * @param {string} url - Full URL or path (path-only returns null)
 * @returns {string|null}
 */
export function getHostnameFromUrl(url) {
  if (typeof url !== 'string' || !url.trim()) {
    return null;
  }
  const trimmed = url.trim();
  if (trimmed.startsWith('//')) {
    return null;
  }
  if (trimmed.startsWith('/')) {
    return null; // path-only, same-origin
  }
  try {
    const parsed = new URL(trimmed, 'https://dummy.example');
    return parsed.hostname && parsed.hostname !== 'dummy.example' ? parsed.hostname : null;
  } catch {
    return null;
  }
}

/**
 * Checks whether a URL's hostname is an allowed CleverPush origin.
 * Only hostnames ending with ALLOWED_SCRIPT_ORIGIN_SUFFIXES are accepted.
 *
 * @param {string} url - Full URL to validate
 * @returns {boolean}
 */
export function isAllowedWorkerScriptOrigin(url) {
  const hostname = getHostnameFromUrl(url);

  if (!hostname) {
    return true; // relative path, same-origin
  }
  const lower = hostname.toLowerCase();
  return ALLOWED_SCRIPT_ORIGIN_SUFFIXES.some(
    (suffix) => lower === suffix.slice(1) || lower.endsWith(suffix)
  );
}

/**
 * Validates apiEndpoint for use in the worker. Only allowed CleverPush API origins are accepted.
 * @param {string} url - API base URL
 * @returns {boolean}
 */
export function isAllowedApiOrigin(url) {
  return isAllowedWorkerScriptOrigin(url);
}
